import { getCurrencySymbol, getDisplayCurrencySymbol, getDisplayExchangeRate } from "./format-price";

export interface UsdPriceScale {
  rate: number;
  symbol: string;
  centsToDisplayFloor: (cents: number) => number;
  centsToDisplayCeil: (cents: number) => number;
  displayToCentsMin: (display: number) => number;
  displayToCentsMax: (display: number) => number;
}

/**
 * The conversion discipline for price controls whose canonical unit is USD
 * cents but whose UI speaks the viewer's display currency (marketplace price
 * filter, destinations budget filter).
 *
 * Per-bound rounding mirrors the floor/ceil used on the way back out, so a
 * committed display value round-trips to itself and a slider thumb doesn't
 * snap by a unit after release under non-1.0 display exchange rates. At the
 * range edges these land at-or-outside the cents bounds, keeping a full-width
 * selection unrefined.
 *
 * When no exchange rate is available the scale stays in USD — amounts AND
 * symbol — rather than dressing USD magnitudes in the display currency's
 * symbol (which would misstate every bound by the missing rate).
 */
export function usdPriceScale(): UsdPriceScale {
  const rate = getDisplayExchangeRate("USD");
  const effective = rate ?? 1;

  return {
    rate: effective,
    symbol: rate === null ? getCurrencySymbol("USD") : getDisplayCurrencySymbol(),
    centsToDisplayFloor: (cents) => Math.floor((cents / 100) * effective),
    centsToDisplayCeil: (cents) => Math.ceil((cents / 100) * effective),
    displayToCentsMin: (display) => Math.ceil((display / effective) * 100),
    displayToCentsMax: (display) => Math.floor((display / effective) * 100),
  };
}
